import React, { useEffect, useState, useCallback } from 'react';
import { Z_INDEX_CLASSES } from '../constants/zIndex.ts';

interface SuccessBannerProps {
  message: string | null;
  user?: {
    name?: string;
    email?: string;
  };
  type?: 'success' | 'welcome' | 'info';
  duration?: number; // Auto-hide duration in milliseconds (0 = no auto-hide)
  onClose?: () => void;
}

const SuccessBanner: React.FC<SuccessBannerProps> = ({
  message,
  user,
  type = 'success',
  duration = 5000,
  onClose
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  const handleClose = useCallback(() => {
    setIsAnimating(false);
    setTimeout(() => {
      setIsVisible(false);
      onClose?.();
    }, 300); // Match animation duration
  }, [onClose]);

  useEffect(() => {
    if (message) {
      setIsVisible(true);
      setIsAnimating(true);
      
      // Auto-hide after duration
      if (duration > 0) {
        const timer = setTimeout(() => {
          handleClose();
        }, duration);
        
        return () => clearTimeout(timer);
      }
    } else {
      setIsVisible(false);
    }
  }, [message, duration, handleClose]);

  if (!isVisible || !message) {
    return null;
  }

  const getBannerStyles = () => {
    switch (type) {
      case 'success':
        return {
          bg: 'bg-green-50 border-green-200',
          text: 'text-green-800',
          icon: 'text-green-600',
          iconPath: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
          title: 'Success'
        };
      case 'welcome':
        return {
          bg: 'bg-gradient-to-r from-green-50 to-blue-50 border-green-300',
          text: 'text-green-800',
          icon: 'text-green-600',
          iconPath: 'M5 13l4 4L19 7',
          title: 'Welcome!'
        };
      case 'info':
        return {
          bg: 'bg-blue-50 border-blue-200',
          text: 'text-blue-800',
          icon: 'text-blue-600',
          iconPath: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
          title: 'Info'
        };
      default:
        return {
          bg: 'bg-gray-50 border-gray-200',
          text: 'text-gray-800',
          icon: 'text-gray-600',
          iconPath: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
          title: 'Notice'
        };
    }
  };

  const styles = getBannerStyles();

  return (
    <div className={`fixed top-4 right-4 ${Z_INDEX_CLASSES.BANNER} max-w-md w-full mx-4 transition-all duration-300 ease-in-out ${
      isAnimating ? 'transform translate-x-0 opacity-100' : 'transform translate-x-full opacity-0'
    }`} style={{ zIndex: 80 }}>
      <div className={`${styles.bg} border rounded-lg shadow-lg p-4`}>
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg className={`w-5 h-5 ${styles.icon}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={styles.iconPath} />
            </svg>
          </div>
          <div className="ml-3 flex-1">
            <h4 className={`text-sm font-semibold ${styles.text} mb-1`}>
              {styles.title}
            </h4>
            <p className={`text-sm ${styles.text}`}>
              {user?.name && type === 'welcome' ? (
                <>
                  Welcome to H2GO Water Booking, <span className="font-semibold">{user.name}</span>! 
                  <br />
                  <span className="text-xs opacity-90 mt-1 block">{message}</span>
                </>
              ) : (
                message
              )}
            </p>
            {user?.name && type === 'welcome' && (
              <div className="mt-2 text-xs text-green-700">
                <p>Your account has been successfully created and verified. You can now place orders and manage your bookings.</p>
              </div>
            )}
          </div>
          <div className="ml-4 flex-shrink-0">
            <button
              onClick={handleClose}
              className={`inline-flex ${styles.icon} hover:opacity-75 transition-opacity`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuccessBanner;
